/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DockerHost,
  DockerContainer,
  getDockerHostContainers,
  startDockerContainer,
  stopDockerContainer,
  restartDockerContainer,
} from "@/api/docker";
import { DOCKER_REFRESH_INTERVALS } from "@/constants/dockerRefreshIntervals";
import { showToast } from "@/components/common/Toast";

interface UseDockerHostOptions {
  host: DockerHost;
}

export const useDockerHost = ({ host }: UseDockerHostOptions) => {
  const queryClient = useQueryClient();

  const containersQuery = useQuery<DockerContainer[]>({
    queryKey: ["docker-host-containers", host.id],
    queryFn: () => getDockerHostContainers(host.id),
    refetchInterval: host.enabled ? DOCKER_REFRESH_INTERVALS.CONTAINERS : false,
    staleTime: DOCKER_REFRESH_INTERVALS.CONTAINERS / 2,
    enabled: host.enabled,
  });

  const invalidateContainers = () => {
    queryClient.invalidateQueries({ queryKey: ["docker-host-containers", host.id] });
  };

  const startMutation = useMutation({
    mutationFn: (containerId: string) => startDockerContainer(host.id, containerId),
    onSuccess: () => {
      invalidateContainers();
      showToast("Container started", "success");
    },
    onError: (error: Error) => {
      showToast("Failed to start container", "error", { description: error.message });
    },
  });

  const stopMutation = useMutation({
    mutationFn: (containerId: string) => stopDockerContainer(host.id, containerId),
    onSuccess: () => {
      invalidateContainers();
      showToast("Container stopped", "success");
    },
    onError: (error: Error) => {
      showToast("Failed to stop container", "error", { description: error.message });
    },
  });

  const restartMutation = useMutation({
    mutationFn: (containerId: string) => restartDockerContainer(host.id, containerId),
    onSuccess: () => {
      invalidateContainers();
      showToast("Container restarted", "success");
    },
    onError: (error: Error) => {
      showToast("Failed to restart container", "error", { description: error.message });
    },
  });

  return {
    containers: containersQuery.data ?? [],
    isLoadingContainers: containersQuery.isLoading,
    startMutation,
    stopMutation,
    restartMutation,
  };
};
